import { err, ok, type Result } from "neverthrow";
import { match, P } from "ts-pattern";

import { dayjs } from "@thalamos/common";

import { FormSectionSchema } from "../types/FormSection.js";
import {
  IncidentFormTypeSchema,
  type IncidentForm,
  type IncidentWorkItemWithContext,
} from "../types/incidentWorkItem.js";
import {
  type RecordPlaceOfSafetyArrivalMinimumFields,
  type RecordPlaceOfSafetyLeaveMinimumFields,
  type RecordPlaceOfSafetyMinimumFields,
  type RecordPolicePresenceMinimumFields,
  type RecordPreEndIncidentMinimumFields,
  type RequestPolicePresenceMinimumFields,
} from "../types/workflow/index.js";

export type CombinePlacesOfSafetyAndOfficerPresenceFormsResult<
  TRecordPlaceOfSafetyFields extends RecordPlaceOfSafetyMinimumFields =
    RecordPlaceOfSafetyMinimumFields,
  TRecordPlaceOfSafetyArrivalFields extends
    RecordPlaceOfSafetyArrivalMinimumFields =
    RecordPlaceOfSafetyArrivalMinimumFields,
  TRecordPlaceOfSafetyLeaveFields extends
    RecordPlaceOfSafetyLeaveMinimumFields =
    RecordPlaceOfSafetyLeaveMinimumFields,
  TRequestPolicePresenceFields extends RequestPolicePresenceMinimumFields =
    RequestPolicePresenceMinimumFields,
  TRecordPolicePresenceFields extends RecordPolicePresenceMinimumFields =
    RecordPolicePresenceMinimumFields,
  TPreEndIncidentFields extends Partial<RecordPreEndIncidentMinimumFields> =
    Partial<RecordPreEndIncidentMinimumFields>,
> = {
  placeOfSafety: {
    form: IncidentForm;
    sections: {
      recordPlaceOfSafetyMain: Partial<TRecordPlaceOfSafetyFields>;
      recordPlaceOfSafetyArrival?:
        Partial<TRecordPlaceOfSafetyArrivalFields> | undefined;
      recordPlaceOfSafetyLeave?:
        Partial<TRecordPlaceOfSafetyLeaveFields> | undefined;
    };
  };
  policePresence: Array<{
    form: IncidentForm;
    sections: {
      requestPolicePresenceMain: TRequestPolicePresenceFields;
      recordPolicePresence?: Partial<TRecordPolicePresenceFields> | undefined;
    };
  }>;
  preEndIncident: Partial<TPreEndIncidentFields> | null;
};

/**
 * Combines the information we have about the places of safety and officer presence forms
 * into a single, sorted array of objects that can be used to populate PDFs or on-screen components.
 * @param incidentWorkItem
 * @returns
 */
export function combinePlacesOfSafetyAndOfficerPresenceForms<
  TRecordPlaceOfSafetyFields extends RecordPlaceOfSafetyMinimumFields =
    RecordPlaceOfSafetyMinimumFields,
  TRecordPlaceOfSafetyArrivalFields extends
    RecordPlaceOfSafetyArrivalMinimumFields =
    RecordPlaceOfSafetyArrivalMinimumFields,
  TRecordPlaceOfSafetyLeaveFields extends
    RecordPlaceOfSafetyLeaveMinimumFields =
    RecordPlaceOfSafetyLeaveMinimumFields,
  TRequestPolicePresenceFields extends RequestPolicePresenceMinimumFields =
    RequestPolicePresenceMinimumFields,
  TRecordPolicePresenceFields extends RecordPolicePresenceMinimumFields =
    RecordPolicePresenceMinimumFields,
>(
  incidentWorkItem: IncidentWorkItemWithContext,
): Result<
  Array<
    CombinePlacesOfSafetyAndOfficerPresenceFormsResult<
      TRecordPlaceOfSafetyFields,
      TRecordPlaceOfSafetyArrivalFields,
      TRecordPlaceOfSafetyLeaveFields,
      TRequestPolicePresenceFields,
      TRecordPolicePresenceFields
    >
  >,
  string
> {
  const preEndIncident = incidentWorkItem.forms
    .find((f) => f.formType === IncidentFormTypeSchema.enum.preEndIncident)
    ?.sections.find((s) => s.id === FormSectionSchema.enum.preEndIncident);
  // Extract all of the places of safety forms
  const placesOfSafetyForms = incidentWorkItem.forms.filter(
    (form) => form.formType === IncidentFormTypeSchema.enum.recordPlaceOfSafety,
  );

  // Extract all of the officer presence forms
  const officerPresenceForms = incidentWorkItem.forms.filter(
    (form) =>
      form.formType === IncidentFormTypeSchema.enum.requestPolicePresence,
  );

  // Quick validation check based on array lengths
  if (placesOfSafetyForms.length === 0) {
    if (officerPresenceForms.length > 0) {
      return err(
        "Cannot allocate officer presence forms if there are no places of safety",
      );
    } else return ok([]);
  }

  // Transform the places of safety forms into the shape we need.
  const placesOfSafety = placesOfSafetyForms.map((form) => {
    return {
      placeOfSafety: {
        form,
        sections: {
          recordPlaceOfSafetyMain: form.sections.find(
            (section) =>
              section.id === FormSectionSchema.enum.recordPlaceOfSafetyMain,
          )?.data as Partial<TRecordPlaceOfSafetyFields>,
          recordPlaceOfSafetyArrival: (form.sections.find(
            (section) =>
              section.id === FormSectionSchema.enum.recordPlaceOfSafetyArrival,
          )?.data ?? undefined) as
            Partial<TRecordPlaceOfSafetyArrivalFields> | undefined,
          recordPlaceOfSafetyLeave: (form.sections.find(
            (section) =>
              section.id === FormSectionSchema.enum.recordPlaceOfSafetyLeave,
          )?.data ?? undefined) as
            Partial<TRecordPlaceOfSafetyLeaveFields> | undefined,
        },
      },
      policePresence: [] as CombinePlacesOfSafetyAndOfficerPresenceFormsResult<
        TRecordPlaceOfSafetyFields,
        TRecordPlaceOfSafetyArrivalFields,
        TRecordPlaceOfSafetyLeaveFields,
        TRequestPolicePresenceFields,
        TRecordPolicePresenceFields
      >["policePresence"],
      preEndIncident:
        preEndIncident &&
        preEndIncident.data !== null &&
        "endingReason" in preEndIncident.data
          ? (preEndIncident.data as RecordPreEndIncidentMinimumFields)
          : null,
    } satisfies CombinePlacesOfSafetyAndOfficerPresenceFormsResult<
      TRecordPlaceOfSafetyFields,
      TRecordPlaceOfSafetyArrivalFields,
      TRecordPlaceOfSafetyLeaveFields,
      TRequestPolicePresenceFields,
      TRecordPolicePresenceFields
    >;
  });

  // Sort the places of safety
  // Order is about the most-recently visited first, and least-recently visited last
  // Specifically, we want to sort by the arrival time, then the created time
  placesOfSafety.sort((a, b) => {
    const aCreated = dayjs.utc(a.placeOfSafety.form.createdAt);
    const bCreated = dayjs.utc(b.placeOfSafety.form.createdAt);
    const aArrival = a.placeOfSafety.sections.recordPlaceOfSafetyArrival
      ?.arrivalDateTime?.value
      ? dayjs.utc(
          a.placeOfSafety.sections.recordPlaceOfSafetyArrival.arrivalDateTime
            .value,
        )
      : undefined;
    const bArrival = b.placeOfSafety.sections.recordPlaceOfSafetyArrival
      ?.arrivalDateTime?.value
      ? dayjs.utc(
          b.placeOfSafety.sections.recordPlaceOfSafetyArrival.arrivalDateTime
            .value,
        )
      : undefined;

    return match([aArrival, bArrival])
      .with([P.nullish, P.nullish], () =>
        aCreated.isBefore(bCreated) ? 1 : -1,
      )
      .with([P.nullish, P.nonNullable], () => 1)
      .with([P.nonNullable, P.nullish], () => -1)
      .with([P.nonNullable, P.nonNullable], ([arrivalA, arrivalB]) =>
        arrivalA.isSame(arrivalB)
          ? aCreated.isBefore(bCreated)
            ? 1
            : -1
          : arrivalA.isBefore(arrivalB)
            ? 1
            : -1,
      )
      .exhaustive();
  });

  // Mapping preserves the earlier non-empty invariant, but TypeScript cannot infer it.
  const mostRecentPlaceOfSafety = placesOfSafety[0];
  if (mostRecentPlaceOfSafety === undefined) {
    return err(
      "Cannot allocate officer presence forms without a place of safety",
    );
  }

  // We need to allocate each of the officer presence forms to exactly one of the places of safety
  officerPresenceForms.forEach((officerPresenceForm) => {
    const payload = {
      form: officerPresenceForm,
      sections: {
        requestPolicePresenceMain: officerPresenceForm.sections.find(
          (section) =>
            section.id === FormSectionSchema.enum.requestPolicePresenceMain,
        )?.data as TRequestPolicePresenceFields,
        recordPolicePresence: (officerPresenceForm.sections.find(
          (section) =>
            section.id === FormSectionSchema.enum.recordPolicePresence,
        )?.data ?? undefined) as TRecordPolicePresenceFields | undefined,
      },
    } satisfies CombinePlacesOfSafetyAndOfficerPresenceFormsResult<
      TRecordPlaceOfSafetyFields,
      TRecordPlaceOfSafetyArrivalFields,
      TRecordPlaceOfSafetyLeaveFields,
      TRequestPolicePresenceFields,
      TRecordPolicePresenceFields
    >["policePresence"][number];

    const requestSection = (officerPresenceForm.sections.find(
      (section) =>
        section.id === FormSectionSchema.enum.requestPolicePresenceMain,
    )?.data ?? undefined) as TRequestPolicePresenceFields | undefined;

    // Have they referenced an existing place of safety?
    // If so, allocate it to that place of safety
    {
      const incidentPlaceOfSafetyId =
        requestSection?.incidentPlaceOfSafetyId?.value ?? undefined;

      if (incidentPlaceOfSafetyId) {
        const placeOfSafety = placesOfSafety.find(
          (pos) => pos.placeOfSafety.form.id === incidentPlaceOfSafetyId,
        );
        if (placeOfSafety) {
          placeOfSafety.policePresence.push(payload);
          return;
        }
      }
    }

    // If they haven't referenced an existing place of safety, we need to
    // allocate it to the place of safety which was the most recent at the time.
    {
      // Find out when the officer presence form was created
      const officerPresenceCreatedAt = dayjs.utc(officerPresenceForm.createdAt);

      // Filter out any places of safety created after the officer presence form
      // was created.
      const placesOfSafetyAfterOfficerPresence = placesOfSafety.filter((pos) =>
        dayjs
          .utc(pos.placeOfSafety.form.createdAt)
          .isBefore(officerPresenceCreatedAt),
      );

      // If there are no places of safety after the officer presence form was created,
      // we need to allocate it to the most recent place of safety.
      const targetPlaceOfSafety =
        placesOfSafetyAfterOfficerPresence[0] ?? mostRecentPlaceOfSafety;

      targetPlaceOfSafety.policePresence.push(payload);
      return;
    }
  });

  // Turn this into the final shape we need
  return ok(
    placesOfSafety.map((pos) => ({
      placeOfSafety: pos.placeOfSafety,
      policePresence: pos.policePresence.sort(
        (a, b) =>
          dayjs.utc(a.form.createdAt).valueOf() -
          dayjs.utc(b.form.createdAt).valueOf(),
      ),
      preEndIncident: incidentWorkItem.forms
        .find((f) => f.formType === IncidentFormTypeSchema.enum.preEndIncident)
        ?.sections.find((s) => s.id === FormSectionSchema.enum.preEndIncident)
        ?.data as RecordPreEndIncidentMinimumFields | null,
    })),
  );
}
